import React, { useState, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { consoleApi } from "../../services/consoleApiService";
import {
  getApiErrorMessage,
  getAdminDisplayName,
  FEEDBACK_IMAGE_TYPES,
  FEEDBACK_IMAGE_MAX_BYTES,
  readFeedbackImageAsBase64,
  toFeedbackImageDataUrl,
} from "../../utils/consoleUtils";
import { ErrorHandler } from "../../utils/errorHandler";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import "../../styles/console.css";

const PRIORITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

export default function FeedbackWidget({ onSubmitted }) {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("medium");
  const [imageData, setImageData] = useState(null); // { base64, contentType }
  const [imageLoading, setImageLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const createdBy = getAdminDisplayName(user);

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setPriority("medium");
    setImageData(null);
    setError(null);
    setSuccessMessage(null);
  };

  const handleFile = async (file) => {
    if (!file) return;
    setError(null);
    setImageLoading(true);
    try {
      const data = await readFeedbackImageAsBase64(file);
      setImageData(data);
    } catch (err) {
      ErrorHandler.logError("FeedbackWidget - handleFile", err);
      setError(err.message || getApiErrorMessage(err, "Could not read image."));
    } finally {
      setImageLoading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const t = (title || "").trim();
    const d = (description || "").trim();
    if (!t) {
      setError("Title is required.");
      return;
    }
    if (!d) {
      setError("Description is required.");
      return;
    }
    setError(null);
    setSuccessMessage(null);
    setSubmitting(true);
    try {
      await consoleApi.feedback.create({
        title: t,
        description: d,
        priority: priority || "medium",
        created_by: createdBy,
        ...(imageData
          ? { image_base64: imageData.base64, image_content_type: imageData.contentType }
          : {}),
      });
      setSuccessMessage("Feedback submitted.");
      resetForm();
      if (typeof onSubmitted === "function") onSubmitted();
      setTimeout(() => {
        setSuccessMessage(null);
        setOpen(false);
      }, 1500);
    } catch (err) {
      ErrorHandler.logError("FeedbackWidget - handleSubmit", err);
      setError(getApiErrorMessage(err, "Failed to submit feedback."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="feedback-trigger"
        onClick={() => setOpen(true)}
        aria-label="Submit feedback"
      >
        Feedback
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="feedback-widget-sheet">
          <SheetHeader>
            <SheetTitle className="feedback-widget-title">Submit feedback</SheetTitle>
          </SheetHeader>
          <form onSubmit={handleSubmit} className="feedback-widget-form">
            <div className="feedback-widget-field">
              <label htmlFor="feedback-title" className="feedback-widget-label">
                Title
              </label>
              <Input
                id="feedback-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Short summary"
                className="feedback-widget-input"
                maxLength={500}
                disabled={submitting}
              />
            </div>
            <div className="feedback-widget-field">
              <label htmlFor="feedback-description" className="feedback-widget-label">
                Description
              </label>
              <Textarea
                id="feedback-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What do you need?"
                className="feedback-widget-textarea"
                rows={4}
                maxLength={10000}
                disabled={submitting}
              />
            </div>
            <div className="feedback-widget-field">
              <label className="feedback-widget-label">Image (optional)</label>
              <div
                className={`feedback-widget-dropzone ${dragOver ? "feedback-widget-dropzone-active" : ""} ${imageData ? "feedback-widget-dropzone-has-image" : ""}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => !imageData && fileInputRef.current?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === " ") && !imageData) fileInputRef.current?.click();
                }}
                aria-label="Add image by drop or click"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={FEEDBACK_IMAGE_TYPES.join(",")}
                  className="feedback-widget-file-input"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFile(file);
                    e.target.value = "";
                  }}
                  aria-hidden
                />
                {imageLoading && <span className="feedback-widget-dropzone-text">Reading…</span>}
                {!imageLoading && imageData && (
                  <>
                    <img
                      src={`data:${imageData.contentType};base64,${imageData.base64}`}
                      alt="Attached"
                      className="feedback-widget-dropzone-preview"
                    />
                    <button
                      type="button"
                      className="feedback-widget-dropzone-remove"
                      onClick={(e) => { e.stopPropagation(); setImageData(null); }}
                      aria-label="Remove image"
                    >
                      Remove
                    </button>
                  </>
                )}
                {!imageLoading && !imageData && (
                  <span className="feedback-widget-dropzone-text">
                    Drag and drop an image, or click to choose (max {FEEDBACK_IMAGE_MAX_BYTES / (1024 * 1024)}MB)
                  </span>
                )}
              </div>
            </div>
            <div className="feedback-widget-field">
              <label htmlFor="feedback-priority" className="feedback-widget-label">
                Priority
              </label>
              <select
                id="feedback-priority"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="feedback-widget-select"
                disabled={submitting}
              >
                {PRIORITY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            {error && <p className="feedback-widget-error" role="alert">{error}</p>}
            {successMessage && <p className="feedback-widget-success" role="status">{successMessage}</p>}
            <div className="feedback-widget-actions">
              <Button type="submit" disabled={submitting} className="feedback-widget-submit">
                {submitting ? "Submitting…" : "Submit"}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>
    </>
  );
}
